import { useState, useEffect, useRef } from "react";
import TopBar from "../components/TopBar";
import { apiFetch } from "../utils/api";

const SECTION_COLORS = {
    Performance: { bg: "#FFF5F0", border: "#FDBA74", dot: "#E8520A", text: "#92400E", light: "#FEF9F6" },
    Interpersonal: { bg: "#EFF6FF", border: "#BFDBFE", dot: "#3B82F6", text: "#1E40AF", light: "#F5F9FF" },
    Values: { bg: "#F0FDF4", border: "#BBF7D0", dot: "#10B981", text: "#065F46", light: "#F6FDFB" },
    Conduct: { bg: "#F5F3FF", border: "#DDD6FE", dot: "#8B5CF6", text: "#4C1D95", light: "#F9F8FF" },
};
const DEFAULT_COLOR = { bg: "#F8FAFC", border: "#E2E8F0", dot: "#64748B", text: "#334155", light: "#FAFBFF" };
const col = s => SECTION_COLORS[s] || DEFAULT_COLOR;

export default function EPRForms({ topBarProps, currentRole }) {
    const [criteria, setCriteria] = useState([]);
    const [sectionOrder, setSectionOrder] = useState([]); // [{ name, sort_order }]
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState("");
    const [toastType, setToastType] = useState("");
    const [activeSection, setActiveSection] = useState(null);
    const [newSectionName, setNewSectionName] = useState("");
    const [addLabel, setAddLabel] = useState("");
    const [adding, setAdding] = useState(false);

    // Reorder modes
    const [secReorderMode, setSecReorderMode] = useState(false); // drag sections
    const [qReorderMode, setQReorderMode] = useState(false);  // drag questions
    const [savingOrder, setSavingOrder] = useState(false);

    // Drag state refs (no re-render needed)
    const dragSec = useRef(null);
    const dragQ = useRef(null);

    // Dragged order (local working copy while in reorder mode)
    const [draftSecOrder, setDraftSecOrder] = useState([]); // section names in order
    const [draftQOrder, setDraftQOrder] = useState([]); // { id, label, section, sort_order }

    // Edit/delete
    const [editItem, setEditItem] = useState(null);
    const [editLabel, setEditLabel] = useState("");
    const [editSection, setEditSection] = useState("");
    const [editCustomSec, setEditCustomSec] = useState("");
    const [saving, setSaving] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const [confirmDelSec, setConfirmDelSec] = useState(null); // section name
    const [deleting, setDeleting] = useState(false);

    const addInputRef = useRef(null);
    const userSelectedSection = useRef(false); // true only when user explicitly clicks a section card
    const isSuperAdmin = currentRole === "Super Admin";

    const showToast = (msg, type = "") => {
        setToast(msg); setToastType(type);
        setTimeout(() => { setToast(""); setToastType(""); }, 2600);
    };

    // ── Load ──────────────────────────────────────────────────────────────────
    const loadCriteria = () => {
        setLoading(true);
        apiFetch("/api/criteria")
            .then(r => r.json())
            .then(d => {
                if (d.success) {
                    setCriteria(d.data);
                    const secs = d.sections || [];
                    setSectionOrder(secs);
                    // Auto-select Performance, fallback to first
                    const names = secs.map(s => s.name);
                    const def = names.includes("Performance") ? "Performance" : (names[0] || null);
                    if (def) setActiveSection(def);
                }
            })
            .catch(() => showToast("Failed to load criteria", "error"))
            .finally(() => setLoading(false));
    };
    useEffect(loadCriteria, []);

    useEffect(() => {
        if (activeSection && activeSection !== "__new__" && userSelectedSection.current)
            setTimeout(() => addInputRef.current?.focus(), 120);
    }, [activeSection]);

    // ── Derived ───────────────────────────────────────────────────────────────
    const sortedSections = sectionOrder.map(s => s.name).filter(n => criteria.some(c => c.section === n));
    const knownSections = [...sortedSections];
    const sectionItems = s => [...criteria.filter(c => c.section === s)]
        .sort((a, b) => (a.sort_order ?? a.id) - (b.sort_order ?? b.id));

    // Global sequential order — re-derived whenever sectionOrder or criteria sort_order changes.
    // Uses sectionOrder for section sequence, then sort_order within each section.
    // This is the single source of truth for question numbers — no reload needed after reorder.
    const globalCriteriaOrder = sortedSections.flatMap(sec => sectionItems(sec));
    const getQuestionNum = id => {
        const pos = globalCriteriaOrder.findIndex(x => x.id === id);
        return pos >= 0 ? pos + 1 : null;
    };

    const toggleSection = s => {
        if (secReorderMode || qReorderMode) return; // block toggle while reordering
        userSelectedSection.current = true;
        setActiveSection(prev => prev === s ? null : s);
        setAddLabel("");
        setQReorderMode(false);
    };

    // ── Section drag ─────────────────────────────────────────────────────────
    const enterSecReorder = () => {
        setSecReorderMode(true);
        setActiveSection(null);
        setDraftSecOrder([...sortedSections]);
    };
    const cancelSecReorder = () => { setSecReorderMode(false); setDraftSecOrder([]); };

    const onSecDragStart = (e, name) => { dragSec.current = name; e.dataTransfer.effectAllowed = "move"; };
    const onSecDragOver = (e, name) => {
        e.preventDefault();
        if (dragSec.current === name) return;
        const arr = [...draftSecOrder];
        const from = arr.indexOf(dragSec.current);
        const to = arr.indexOf(name);
        if (from < 0 || to < 0) return;
        arr.splice(from, 1);
        arr.splice(to, 0, dragSec.current);
        setDraftSecOrder(arr);
    };
    const onSecDrop = e => { e.preventDefault(); };

    const saveSecOrder = async () => {
        setSavingOrder(true);
        try {
            const res = await apiFetch("/api/criteria/sections-order", {
                method: "PUT", body: JSON.stringify({ sections: draftSecOrder }),
            });
            const d = await res.json();
            if (!d.success) { showToast(d.message || "Save failed", "error"); return; }
            setSectionOrder(draftSecOrder.map((name, i) => ({ name, sort_order: i + 1 })));
            setSecReorderMode(false);
            setDraftSecOrder([]);
            showToast("Section order saved ✓");
        } catch { showToast("Network error", "error"); }
        finally { setSavingOrder(false); }
    };

    // ── Question drag ─────────────────────────────────────────────────────────
    const enterQReorder = (section) => {
        setQReorderMode(true);
        setDraftQOrder(sectionItems(section));
    };
    const cancelQReorder = () => { setQReorderMode(false); setDraftQOrder([]); };

    const onQDragStart = (e, id) => { dragQ.current = id; e.dataTransfer.effectAllowed = "move"; };
    const onQDragOver = (e, id) => {
        e.preventDefault();
        if (dragQ.current === id) return;
        const arr = [...draftQOrder];
        const from = arr.findIndex(x => x.id === dragQ.current);
        const to = arr.findIndex(x => x.id === id);
        if (from < 0 || to < 0) return;
        const [item] = arr.splice(from, 1);
        arr.splice(to, 0, item);
        setDraftQOrder(arr);
    };
    const onQDrop = e => { e.preventDefault(); };

    const saveQOrder = async () => {
        if (!activeSection) return;
        setSavingOrder(true);
        try {
            const res = await apiFetch("/api/criteria/bulk-order", {
                method: "PUT",
                body: JSON.stringify({ section: activeSection, ids: draftQOrder.map(q => q.id) }),
            });
            const d = await res.json();
            if (!d.success) { showToast(d.message || "Save failed", "error"); return; }
            // Update local criteria sort_order
            setCriteria(prev => prev.map(c => {
                const newIdx = draftQOrder.findIndex(q => q.id === c.id);
                return newIdx >= 0 ? { ...c, sort_order: newIdx + 1 } : c;
            }));
            setQReorderMode(false);
            setDraftQOrder([]);
            showToast("Question order saved ✓");
        } catch { showToast("Network error", "error"); }
        finally { setSavingOrder(false); }
    };

    // ── Add question ──────────────────────────────────────────────────────────
    const handleAdd = async (section) => {
        const lbl = addLabel.trim();
        if (!lbl) { showToast("Label is required", "error"); return; }
        setAdding(true);
        try {
            const res = await apiFetch("/api/criteria", { method: "POST", body: JSON.stringify({ label: lbl, section }) });
            const d = await res.json();
            if (!d.success) { showToast(d.message || "Add failed", "error"); return; }
            setCriteria(prev => [...prev, { id: d.id, label: lbl, section, sort_order: 999 }]);
            setAddLabel("");
            showToast("Question added ✓");
            setTimeout(() => addInputRef.current?.focus(), 80);
        } catch { showToast("Network error", "error"); }
        finally { setAdding(false); }
    };

    // ── Add new section ───────────────────────────────────────────────────────
    const handleAddNewSection = async () => {
        const sec = newSectionName.trim();
        const lbl = addLabel.trim();
        if (!sec) { showToast("Section name is required", "error"); return; }
        if (!lbl) { showToast("First question is required", "error"); return; }
        if (knownSections.includes(sec)) { showToast("Section already exists", "error"); return; }
        setAdding(true);
        try {
            const res = await apiFetch("/api/criteria", { method: "POST", body: JSON.stringify({ label: lbl, section: sec }) });
            const d = await res.json();
            if (!d.success) { showToast(d.message || "Add failed", "error"); return; }
            setCriteria(prev => [...prev, { id: d.id, label: lbl, section: sec, sort_order: 1 }]);
            setSectionOrder(prev => [...prev, { name: sec, sort_order: prev.length + 1 }]);
            setActiveSection(sec);
            setNewSectionName(""); setAddLabel("");
            showToast(`Section "${sec}" created ✓`);
        } catch { showToast("Network error", "error"); }
        finally { setAdding(false); }
    };

    // ── Edit ──────────────────────────────────────────────────────────────────
    const handleEditSave = async () => {
        const lbl = editLabel.trim();
        const sec = editSection === "__custom__" ? editCustomSec.trim() : editSection;
        if (!lbl || !sec) { showToast("Label and section are required", "error"); return; }
        setSaving(true);
        try {
            const res = await apiFetch(`/api/criteria/${editItem.id}`, { method: "PUT", body: JSON.stringify({ label: lbl, section: sec }) });
            const d = await res.json();
            if (!d.success) { showToast(d.message || "Save failed", "error"); return; }
            setCriteria(prev => {
                const sectionChanged = editItem.section !== sec;
                if (sectionChanged) {
                    // Place at end of new section: max sort_order of new section + 1
                    const maxOrder = Math.max(0, ...prev.filter(x => x.section === sec && x.id !== editItem.id).map(x => x.sort_order ?? 0));
                    return prev.map(c => c.id === editItem.id ? { ...c, label: lbl, section: sec, sort_order: maxOrder + 1 } : c);
                }
                return prev.map(c => c.id === editItem.id ? { ...c, label: lbl } : c);
            });
            if (!knownSections.includes(sec)) setSectionOrder(prev => [...prev, { name: sec, sort_order: prev.length + 1 }]);
            setEditItem(null);
            showToast("Question updated ✓");
        } catch { showToast("Network error", "error"); }
        finally { setSaving(false); }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await apiFetch(`/api/criteria/${confirmDel.id}`, { method: "DELETE" });
            const d = await res.json();
            if (!d.success) { showToast(d.message || "Delete failed", "error"); setConfirmDel(null); return; }
            setCriteria(prev => prev.filter(c => c.id !== confirmDel.id));
            // If last question in section was deleted, remove section from local state too
            if (d.sectionDeleted) {
                setSectionOrder(prev => prev.filter(s => s.name !== d.section));
                if (activeSection === d.section) setActiveSection(null);
            }
            setConfirmDel(null);
            showToast(d.sectionDeleted ? `Question deleted — section "${d.section}" removed` : "Question deleted");
        } catch { showToast("Network error", "error"); }
        finally { setDeleting(false); }
    };

    const handleDeleteSection = async () => {
        const sec = confirmDelSec;
        setDeleting(true);
        try {
            const res = await apiFetch(`/api/criteria/section/${encodeURIComponent(sec)}`, { method: "DELETE" });
            const d = await res.json();
            if (!d.success) { showToast(d.message || "Delete failed", "error"); setConfirmDelSec(null); return; }
            setCriteria(prev => prev.filter(c => c.section !== sec));
            setSectionOrder(prev => prev.filter(s => s.name !== sec));
            if (activeSection === sec) setActiveSection(sortedSections.find(s => s !== sec) || null);
            setConfirmDelSec(null);
            showToast(`Section "${sec}" deleted`);
        } catch { showToast("Network error", "error"); }
        finally { setDeleting(false); }
    };

    const openEdit = item => {
        setEditItem(item); setEditLabel(item.label);
        const known = knownSections.includes(item.section);
        setEditSection(known ? item.section : "__custom__");
        setEditCustomSec(known ? "" : item.section);
    };

    const inp = {
        padding: "9px 12px", borderRadius: 8, border: "1.5px solid #E2E8F0",
        fontSize: 13, fontFamily: "'DM Sans',sans-serif", background: "#F8FAFC",
        color: "#0D1B2A", outline: "none", width: "100%", boxSizing: "border-box",
    };

    const displaySections = secReorderMode ? draftSecOrder : sortedSections;

    return (
        <div className="fade-in">
            <TopBar title="EPR Forms" subtitle="Manage review form sections and criteria questions" {...topBarProps} />

            {toast && (
                <div style={{
                    position: "fixed", top: 18, right: 24, zIndex: 9999,
                    background: toastType === "error" ? "#FEF2F2" : "#F0FDF4",
                    border: `1.5px solid ${toastType === "error" ? "#FECACA" : "#BBF7D0"}`,
                    color: toastType === "error" ? "#B91C1C" : "#065F46",
                    borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 600,
                    boxShadow: "0 4px 20px rgba(0,0,0,.12)"
                }}>{toast}</div>
            )}

            <div style={{ padding: "0 24px 40px" }}>
                {loading ? (
                    <div style={{ textAlign: "center", padding: 80, color: "#94A3B8", fontSize: 14 }}>Loading…</div>
                ) : (
                    <>
                        {/* ── Sections header bar ── */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#64748B" }}>
                                {secReorderMode ? "✋ Drag sections to reorder, then save" : `${sortedSections.length} section${sortedSections.length !== 1 ? "s" : ""} · ${criteria.length} total question${criteria.length !== 1 ? "s" : ""}`}
                            </div>
                            {isSuperAdmin && (
                                secReorderMode ? (
                                    <div style={{ display: "flex", gap: 8 }}>
                                        <button className="btn-secondary" style={{ fontSize: 12 }} onClick={cancelSecReorder} disabled={savingOrder}>Cancel</button>
                                        <button className="btn-primary" style={{ fontSize: 12 }} onClick={saveSecOrder} disabled={savingOrder}>
                                            {savingOrder ? "Saving…" : "💾 Save Section Order"}
                                        </button>
                                    </div>
                                ) : (
                                    <button className="btn-secondary" style={{ fontSize: 12 }} onClick={enterSecReorder}>⠿ Reorder Sections</button>
                                )
                            )}
                        </div>

                        {/* ── Section cards grid ── */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
                            {displaySections.map(s => {
                                const c = col(s);
                                const isActive = activeSection === s && !secReorderMode;
                                const count = sectionItems(s).length;
                                return (
                                    <div key={s}
                                        draggable={secReorderMode}
                                        onDragStart={secReorderMode ? e => onSecDragStart(e, s) : undefined}
                                        onDragOver={secReorderMode ? e => onSecDragOver(e, s) : undefined}
                                        onDrop={secReorderMode ? onSecDrop : undefined}
                                        onClick={() => !secReorderMode && toggleSection(s)}
                                        style={{
                                            background: secReorderMode ? "#F8FAFC" : (isActive ? c.bg : "#fff"),
                                            border: `2px solid ${secReorderMode ? "#CBD5E1" : (isActive ? c.dot : "#E2E8F0")}`,
                                            borderRadius: 14, padding: "18px 20px",
                                            cursor: secReorderMode ? "grab" : "pointer",
                                            transition: "all .15s",
                                            boxShadow: isActive ? `0 0 0 4px ${c.dot}22` : "none",
                                            transform: isActive ? "translateY(-2px)" : "none",
                                            userSelect: "none",
                                        }}
                                        onMouseEnter={e => { if (!secReorderMode && !isActive) { e.currentTarget.style.borderColor = c.dot; e.currentTarget.style.background = c.light; } }}
                                        onMouseLeave={e => { if (!secReorderMode && !isActive) { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.background = "#fff"; } }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                                            {secReorderMode && <span style={{ fontSize: 14, color: "#94A3B8", marginRight: 2 }}>⠿</span>}
                                            <span style={{ width: 9, height: 9, borderRadius: "50%", background: secReorderMode ? "#94A3B8" : c.dot, flexShrink: 0 }} />
                                            <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 13, color: secReorderMode ? "#64748B" : (isActive ? c.text : "#0D1B2A") }}>{s}</span>
                                        </div>
                                        <div style={{ fontSize: 26, fontWeight: 800, color: secReorderMode ? "#94A3B8" : c.dot, lineHeight: 1 }}>{count}</div>
                                        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>question{count !== 1 ? "s" : ""}</div>
                                        {!secReorderMode && (
                                            <div style={{ marginTop: 10, fontSize: 10, color: isActive ? c.text : "#94A3B8", fontWeight: 600 }}>
                                                {isActive ? "▲ collapse" : "▼ view questions"}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* + New Section card */}
                            {isSuperAdmin && !secReorderMode && (
                                <div
                                    onClick={() => { setActiveSection(prev => prev === "__new__" ? null : "__new__"); setAddLabel(""); setNewSectionName(""); setQReorderMode(false); }}
                                    style={{
                                        background: activeSection === "__new__" ? "#F0FDF4" : "#fff",
                                        border: `2px dashed ${activeSection === "__new__" ? "#10B981" : "#CBD5E1"}`,
                                        borderRadius: 14, padding: "18px 20px", cursor: "pointer",
                                        transition: "all .15s", display: "flex", flexDirection: "column",
                                        alignItems: "center", justifyContent: "center", gap: 6, minHeight: 120,
                                        boxShadow: activeSection === "__new__" ? "0 0 0 4px #10B98122" : "none",
                                    }}
                                    onMouseEnter={e => { if (activeSection !== "__new__") { e.currentTarget.style.borderColor = "#10B981"; e.currentTarget.style.background = "#F6FDFB"; } }}
                                    onMouseLeave={e => { if (activeSection !== "__new__") { e.currentTarget.style.borderColor = "#CBD5E1"; e.currentTarget.style.background = "#fff"; } }}>
                                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: activeSection === "__new__" ? "#D1FAE5" : "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: activeSection === "__new__" ? "#10B981" : "#94A3B8" }}>+</div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: activeSection === "__new__" ? "#065F46" : "#64748B" }}>New Section</div>
                                </div>
                            )}
                        </div>

                        {/* ── Expanded section questions panel ── */}
                        {activeSection && activeSection !== "__new__" && !secReorderMode && (() => {
                            const c = col(activeSection);
                            const items = qReorderMode ? draftQOrder : sectionItems(activeSection);
                            return (
                                <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 16, border: `2px solid ${c.border}` }}>
                                    {/* Section panel header */}
                                    <div style={{ background: c.bg, padding: "12px 20px", borderBottom: `1.5px solid ${c.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
                                        <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: c.text, flex: 1 }}>{activeSection}</span>
                                        <span style={{ fontSize: 12, color: c.text, opacity: 0.7, marginRight: 8 }}>{items.length} question{items.length !== 1 ? "s" : ""}</span>
                                        {/* Reorder / Save / Cancel buttons */}
                                        {isSuperAdmin && (
                                            qReorderMode ? (
                                                <div style={{ display: "flex", gap: 8 }}>
                                                    <button className="btn-secondary" style={{ fontSize: 11, padding: "5px 10px" }} onClick={cancelQReorder} disabled={savingOrder}>Cancel</button>
                                                    <button className="btn-primary" style={{ fontSize: 11, padding: "5px 12px" }} onClick={saveQOrder} disabled={savingOrder}>
                                                        {savingOrder ? "Saving…" : "💾 Save Order"}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div style={{ display: "flex", gap: 6 }}>
                                                    {items.length > 1 && (
                                                        <button className="btn-secondary" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => enterQReorder(activeSection)}>
                                                            ⠿ Reorder
                                                        </button>
                                                    )}
                                                    <button className="btn-danger" style={{ fontSize: 11, padding: "5px 10px" }}
                                                        onClick={() => setConfirmDelSec(activeSection)}>
                                                        Delete Section
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>

                                    {/* Questions */}
                                    {items.length === 0 ? (
                                        <div style={{ padding: "24px 20px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>No questions yet — add the first one below.</div>
                                    ) : (
                                        items.map((item, idx) => {
                                            const globalNum = getQuestionNum(item.id) ?? (idx + 1);
                                            return (
                                                <div key={item.id}
                                                    draggable={qReorderMode}
                                                    onDragStart={qReorderMode ? e => onQDragStart(e, item.id) : undefined}
                                                    onDragOver={qReorderMode ? e => onQDragOver(e, item.id) : undefined}
                                                    onDrop={qReorderMode ? onQDrop : undefined}
                                                    style={{
                                                        display: "flex", alignItems: "center", gap: 12, padding: "12px 20px",
                                                        borderBottom: idx < items.length - 1 || isSuperAdmin ? "1px solid #F1F5F9" : "none",
                                                        background: "#fff", transition: "background .15s",
                                                        cursor: qReorderMode ? "grab" : "default",
                                                    }}
                                                    onMouseEnter={e => { if (!qReorderMode) e.currentTarget.style.background = c.light; }}
                                                    onMouseLeave={e => { if (!qReorderMode) e.currentTarget.style.background = "#fff"; }}>

                                                    {/* Drag handle (reorder mode) or number badge (normal) */}
                                                    {qReorderMode ? (
                                                        <span style={{ fontSize: 16, color: "#94A3B8", cursor: "grab", flexShrink: 0, userSelect: "none" }}>⠿</span>
                                                    ) : (
                                                        <div style={{
                                                            width: 26, height: 26, borderRadius: "50%", background: c.bg,
                                                            border: `1.5px solid ${c.border}`, display: "flex", alignItems: "center",
                                                            justifyContent: "center", fontSize: 11, fontWeight: 700, color: c.text, flexShrink: 0
                                                        }}>{globalNum}</div>
                                                    )}

                                                    <div style={{ flex: 1, fontSize: 14, color: qReorderMode ? "#64748B" : "#0D1B2A", fontWeight: 500 }}>{item.label}</div>

                                                    {/* Edit/Delete — hidden in reorder mode */}
                                                    {isSuperAdmin && !qReorderMode && (
                                                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                                            <button className="btn-secondary" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => openEdit(item)}>Edit</button>
                                                            <button className="btn-danger" style={{ fontSize: 11, padding: "4px 8px" }} onClick={() => setConfirmDel({ id: item.id, label: item.label, section: item.section })}>Delete</button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}

                                    {/* Add question row — hidden in reorder mode */}
                                    {isSuperAdmin && !qReorderMode && (
                                        <div style={{ padding: "12px 20px", background: c.light, display: "flex", gap: 10, alignItems: "center" }}>
                                            <input ref={addInputRef} style={{ ...inp, flex: 1, background: "#fff" }}
                                                placeholder={`Add a question to ${activeSection}…`}
                                                value={addLabel} onChange={e => setAddLabel(e.target.value)}
                                                onKeyDown={e => e.key === "Enter" && handleAdd(activeSection)} />
                                            <button className="btn-primary" style={{ fontSize: 12, padding: "9px 16px", whiteSpace: "nowrap" }}
                                                onClick={() => handleAdd(activeSection)} disabled={adding}>
                                                {adding ? "Adding…" : "+ Add"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* ── New section panel ── */}
                        {activeSection === "__new__" && isSuperAdmin && !secReorderMode && (
                            <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 16, border: "2px solid #BBF7D0" }}>
                                <div style={{ background: "#F0FDF4", padding: "14px 20px", borderBottom: "1.5px solid #BBF7D0", display: "flex", alignItems: "center", gap: 10 }}>
                                    <span style={{ fontSize: 16 }}>✨</span>
                                    <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#065F46" }}>Create New Section</span>
                                </div>
                                <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>
                                    <div>
                                        <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>Section Name</label>
                                        <input style={inp} placeholder="e.g. Leadership, Technical Skills…" value={newSectionName} onChange={e => setNewSectionName(e.target.value)} autoFocus />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>First Question <span style={{ color: "#EF4444" }}>*</span></label>
                                        <div style={{ display: "flex", gap: 10 }}>
                                            <input ref={addInputRef} style={{ ...inp, flex: 1 }}
                                                placeholder="Enter the first question for this section…"
                                                value={addLabel} onChange={e => setAddLabel(e.target.value)}
                                                onKeyDown={e => e.key === "Enter" && handleAddNewSection()} />
                                            <button className="btn-primary" style={{ fontSize: 13, whiteSpace: "nowrap", padding: "9px 18px" }}
                                                onClick={handleAddNewSection} disabled={adding}>
                                                {adding ? "Creating…" : "Create Section"}
                                            </button>
                                        </div>
                                        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 6 }}>You can add more questions after the section is created.</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── Edit Modal ── */}
            {editItem && (
                <div className="modal-overlay" style={{ left: 236 }}>
                    <div className="modal" style={{ maxWidth: 480, width: "min(480px, calc(100% - 32px))" }} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: "26px 28px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
                                <div style={{ width: 44, height: 44, borderRadius: 12, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>✏️</div>
                                <div>
                                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#0D1B2A" }}>Edit Question</div>
                                    <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>Question #{getQuestionNum(editItem.id) ?? editItem.id} · {editItem.section}</div>
                                </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                <div>
                                    <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>Question Label</label>
                                    <input style={inp} value={editLabel} onChange={e => setEditLabel(e.target.value)} autoFocus />
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>Section</label>
                                    <select style={{ ...inp, cursor: "pointer" }} value={editSection} onChange={e => setEditSection(e.target.value)}>
                                        {knownSections.map(s => <option key={s} value={s}>{s}</option>)}
                                        <option value="__custom__">+ New section…</option>
                                    </select>
                                </div>
                                {editSection === "__custom__" && (
                                    <div>
                                        <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>New Section Name</label>
                                        <input style={inp} value={editCustomSec} onChange={e => setEditCustomSec(e.target.value)} placeholder="e.g. Leadership" />
                                    </div>
                                )}
                            </div>
                            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
                                <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setEditItem(null)}>Cancel</button>
                                <button className="btn-primary" style={{ flex: 1 }} onClick={handleEditSave} disabled={saving}>
                                    {saving ? "Saving…" : "Save Changes"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delete Section Confirm ── */}
            {confirmDelSec && (
                <div className="modal-overlay" style={{ left: 236 }}>
                    <div className="modal" style={{ maxWidth: 440, width: "min(440px, calc(100% - 32px))" }} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: "28px 28px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                                <div style={{ width: 48, height: 48, borderRadius: 12, background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🗂️</div>
                                <div>
                                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#0D1B2A" }}>Delete Section?</div>
                                    <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>{confirmDelSec}</div>
                                </div>
                            </div>
                            <div style={{ background: "#FFF5F0", border: "1.5px solid #FDBA74", borderRadius: 10, padding: "12px 14px", marginBottom: 18, fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
                                ⚠ All <strong>{sectionItems(confirmDelSec).length} question{sectionItems(confirmDelSec).length !== 1 ? "s" : ""}</strong> in <strong>"{confirmDelSec}"</strong> will be permanently deleted. Sections with existing review scores cannot be deleted.
                            </div>
                            <div style={{ display: "flex", gap: 10 }}>
                                <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirmDelSec(null)} disabled={deleting}>Cancel</button>
                                <button style={{ flex: 1, background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.7 : 1 }}
                                    onClick={handleDeleteSection} disabled={deleting}>
                                    {deleting ? "Deleting…" : "Yes, Delete Section"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delete Confirm ── */}
            {confirmDel && (
                <div className="modal-overlay" style={{ left: 236 }}>
                    <div className="modal" style={{ maxWidth: 420, width: "min(420px, calc(100% - 32px))" }} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: "28px 28px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                                <div style={{ width: 48, height: 48, borderRadius: 12, background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🗑️</div>
                                <div>
                                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#0D1B2A" }}>
                                        {confirmDel && sectionItems(confirmDel.section).length === 1 ? "Delete Question & Section?" : "Delete Question?"}
                                    </div>
                                    <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>Question #{getQuestionNum(confirmDel.id) ?? confirmDel.id}</div>
                                </div>
                            </div>
                            {(() => {
                                const isLastInSection = confirmDel && sectionItems(confirmDel.section).length === 1;
                                return (
                                    <div style={{ background: "#FFF5F0", border: "1.5px solid #FDBA74", borderRadius: 10, padding: "12px 14px", marginBottom: 18, fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
                                        {isLastInSection ? (
                                            <>⚠ "<strong>{confirmDel.label}</strong>" is the <strong>last question</strong> in the <strong>"{confirmDel.section}"</strong> section. Deleting it will also <strong>permanently remove the entire section</strong>. This cannot be undone.</>
                                        ) : (
                                            <>⚠ "<strong>{confirmDel.label}</strong>" will be permanently deleted. Questions with existing review scores cannot be deleted.</>
                                        )}
                                    </div>
                                );
                            })()}
                            <div style={{ display: "flex", gap: 10 }}>
                                <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirmDel(null)} disabled={deleting}>Cancel</button>
                                <button style={{ flex: 1, background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.7 : 1 }}
                                    onClick={handleDelete} disabled={deleting}>
                                    {deleting ? "Deleting…" : "Yes, Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}